import React, { useCallback, useMemo } from 'react';
import { Box, Dialog, DialogProps } from '@fivehealth/botero';

const ModalContext = React.createContext<{
  openModal: (component: React.ReactNode, props?: DialogProps) => void;
  closeModal: () => void;
} | null>(null);

export const useModal = () => {
  const context = React.useContext(ModalContext);
  if (!context) {
    throw new Error('useModal is outside ModalProvider');
  }
  return context;
};

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [content, setContent] = React.useState<React.ReactNode | null>(null);
  const [dialogProps, setDialogProps] = React.useState<DialogProps>();

  /**
   * Open dialog modal provider.
   * @param component - React JSX element. <br/><br/>
   * @param props - {open, onClose, renderHeader, renderFooter, type, title, footerOverrides, isLoading, isFormValid, styleOverrides, headerProps, containerProps, bodyProps, footerProps, overlayProps}<br/> <br/> Refer to the botero Dialog component for details.
   */
  const openModal = useCallback((component: React.ReactNode, props?: DialogProps) => {
    setOpen(true);
    setContent(component);
    setDialogProps(props);
  }, []);

  /**
   * Close dialog modal provider.
   */
  const closeModal = useCallback(() => {
    setContent(null);
    setOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      openModal,
      closeModal,
    }),
    [openModal, closeModal]
  );
  return useMemo(
    () => (
      <ModalContext.Provider value={value}>
        <Box id="modalContainer">
          <Dialog {...dialogProps} onClose={closeModal} open={open}>
            {content}
          </Dialog>
          {children}
        </Box>
      </ModalContext.Provider>
    ),
    [children, closeModal, content, dialogProps, open, value]
  );
}

export default ModalProvider;
